import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getMaxAssessments, getPlanLimits, UNLIMITED } from '@/lib/entitlements';
import type { PlanTier } from '@/lib/billing';

export interface UsageCounts {
  assessmentsThisMonth: number;
  maxAssessments: number;
  pupils: number;
  maxPupils: number;
  classes: number;
  maxClasses: number;
  loading: boolean;
}

interface Params {
  userId: string | undefined;
  schoolId: string | undefined;
  planTier: PlanTier | null;
  planSubTier: string | null;
  isSchoolAdmin: boolean;
}

export function useUsageCounts({ userId, schoolId, planTier, planSubTier, isSchoolAdmin }: Params): UsageCounts {
  const limits = getPlanLimits(planTier);
  const maxAssessments = getMaxAssessments(planTier, planSubTier);

  const [counts, setCounts] = useState({ assessmentsThisMonth: 0, pupils: 0, classes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !schoolId) {
      setLoading(false);
      return;
    }

    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);

    async function fetchCounts() {
      try {
        if (isSchoolAdmin && planTier === 'school') {
          const { data: monthlyCount } = await supabase
            .rpc('get_school_monthly_assessments', { p_school_id: schoolId });
          setCounts({ assessmentsThisMonth: monthlyCount ?? 0, pupils: 0, classes: 0 });
        } else {
          // Free / individual: own records only
          const [assessResult, tcResult] = await Promise.all([
            supabase
              .from('writing_samples')
              .select('id', { count: 'exact', head: true })
              .eq('uploaded_by', userId)
              .gte('created_at', monthStart.toISOString()),
            supabase
              .from('teacher_classes')
              .select('class_id')
              .eq('user_id', userId),
          ]);

          const classIds = (tcResult.data ?? []).map((r: { class_id: string }) => r.class_id);
          const pupilResult = classIds.length > 0
            ? await supabase
                .from('pupils')
                .select('id', { count: 'exact', head: true })
                .in('class_id', classIds)
                .eq('is_archived', false)
            : { count: 0 };

          setCounts({
            assessmentsThisMonth: assessResult.count ?? 0,
            pupils: (pupilResult as { count: number | null }).count ?? 0,
            classes: classIds.length,
          });
        }
      } catch (err) {
        console.error('useUsageCounts error:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchCounts();
  }, [userId, schoolId, planTier, planSubTier, isSchoolAdmin]);

  return {
    ...counts,
    maxAssessments,
    maxPupils: limits.maxPupils,
    maxClasses: limits.maxClasses,
    loading,
  };
}
